import { Component, EventEmitter, OnDestroy, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { UntypedFormBuilder } from '@angular/forms';
import { Sort } from '@angular/material/sort';
import { DeviceDetectorService } from 'ngx-device-detector';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { AppInfoService } from 'core/app-info/app-info.service';
import { AppStateService } from 'core/app-state/app-state.service';
import { SelectedAccountManagerService } from 'core/selected-account-manager/selected-account-manager.service';
import { AccountManager } from 'src/app/modules/shared/entities/account-manager.class';
import { AccountsFilterType } from 'src/app/modules/shared/entities/accounts-filter-type';
import { MatDialog } from '@angular/material/dialog';
import { StorageValues } from '../../../../shared/entities/storage-values.enum';
import { AccountsTemplateValues } from '../../entities/accounts-template-values';
import { AccountsListService } from '../../services/accounts-list.service';
import { AccountsAdvancedSearchComponent } from '../accounts-advanced-search/accounts-advanced-search.component';

@Component({
  selector: 'app-accounts-filter-form',
  templateUrl: './accounts-filter-form.component.html',
  styleUrls: ['./accounts-filter-form.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
export class AccountsListFilterFormComponent implements OnInit, OnDestroy {
  subs = new Subscription();
  templateValues: AccountsTemplateValues;
  templateDataState: any;
  templateCountriesValues: any;

  lnStatusOptions: { value: number, label: string }[] = [];
  formFields = {
    displayedSearchValue: '',
    searchField: '',
    searchBy: 1,
    leads: true,
    myCustomers: true,
    accounts: true,
    delivery: true,
    filterByInterval: 'all',
    lnStatus: 0,
    priceMatrix: '',
    city: '',
    stateId: 0,
    country: 0,
    postalCode: '',
    accountManager: '',
  };
  cachedSearchValues;
  cachedSort;
  clearFiltersNeeded;
  isAccountManager: boolean;
  showMyCustomersCheckbox: boolean; // currently only users that have ONLY the accountManager role should see the checkbox
  currentSearchText = '';
  searchForm = this.fb.group(this.formFields);
  appStrings = this.appInfoService.appStrings;
  intervalOptions = [
    { value: 'all', label: this.appInfoService.appStrings.accounts_optionsIntervalAll },
    { value: 'today', label: this.appInfoService.appStrings.accounts_optionsIntervalDay },
    { value: 'week', label: this.appInfoService.appStrings.accounts_optionsIntervalWeek }
  ];
  isOnline: boolean;
  defaultAccountManager: any;
  allAMOption = {
    name: 'All',
    employeeId: '0'
  };

  @Output() resetSort = new EventEmitter();

  constructor(
    private fb: UntypedFormBuilder,
    private accountsListService: AccountsListService,
    private appInfoService: AppInfoService,
    private appStateService: AppStateService,
    private dialog: MatDialog,
    private router: Router,
    private deviceService: DeviceDetectorService,
    private selectedAccountManagerService: SelectedAccountManagerService
  ) { }

  ngOnInit() {
    this.isAccountManager = this.appInfoService.isAccountManager();
    this.showMyCustomersCheckbox = this.appInfoService.isOnlyAccountManager();
    this.subs.add(
      this.accountsListService.getTemplateValuesWithStates().subscribe((templateValues: [any, any, any]) => {
        this.templateValues = templateValues[0];
        this.templateDataState = templateValues[1];
        this.templateCountriesValues = templateValues[2];
        this.setFormOptions();
        this.setStickyFilters();
        this.setStateIdToNullIfZeroOrEmpty();
        this.accountsListService.getAccountList(this.cachedSearchValues || this.searchForm.value, this.cachedSort);
      })
    );

    this.isOnline = this.appStateService.state.isOnline;
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  setStickyFilters() {
    this.cachedSearchValues = sessionStorage.getItem(StorageValues.CachedFilters + StorageValues.AccountsKey);
    this.cachedSort = sessionStorage.getItem(StorageValues.CachedSort + StorageValues.AccountsKey);
    this.cachedSearchValues = JSON.parse(this.cachedSearchValues);
    this.cachedSort = JSON.parse(this.cachedSort);
    if (this.cachedSort) {
      // reset sorting arrow
      setTimeout(() => this.resetSort.next(this.cachedSort));
    }
    if (this.cachedSearchValues) {
      this.searchForm.patchValue(this.cachedSearchValues);
      this.currentSearchText = this.cachedSearchValues.displayedSearchValue;
      this.clearFiltersNeeded = true;
    } else {
      this.initAccountManagerField();
    }
  }

  /**
   * Sets the default account manager and initializes inner form with it
   */
  private initAccountManagerField(shouldClearFilters?: boolean) {
    if (this.isAccountManager) {
      return;
    }

    const cachedAccountManager = !shouldClearFilters
      ? this.normalizeAccountManager(this.cachedSearchValues?.accountManager)
      : null;
    const selectedAccountManager = this.selectedAccountManagerService.selected;

    this.defaultAccountManager = cachedAccountManager || selectedAccountManager || this.allAMOption;

    this.searchForm.controls.accountManager.setValue(this.defaultAccountManager);
  }

  clearFilters() {
    this.clearFiltersNeeded = false;
    this.currentSearchText = '';

    if (!this.isAccountManager) {
      this.formFields.myCustomers = false;
    }

    this.searchForm.setValue(this.formFields);
    this.searchForm.markAsDirty();

    this.selectedAccountManagerService.clear();
    this.initAccountManagerField(true);
    sessionStorage.removeItem(StorageValues.CachedFilters + StorageValues.AccountsKey);
    sessionStorage.removeItem(StorageValues.CachedSort + StorageValues.AccountsKey);
    this.search();
  }


  onMyCustomersChange() {
    this.searchForm.markAsDirty();
    this.search(null, true);
  }

  /**
   * Searches from the search textbox
   */
  searchText() {
    if (!this.searchForm.dirty) {
      return;
    }
    this.search(null, true);
  }

  /**
   * Debounces the search call for multiple checkbox toggle searches
   */
  /**
   * Searches from the fields
   */
  search(sort?: Sort, shouldCache?: boolean) {
    if (!this.searchForm.dirty && !sort) {
      return;
    }

    this.setStateIdToNullIfZeroOrEmpty();
    const searchValues = this.searchForm.value;

    this.currentSearchText = searchValues.displayedSearchValue;

    // reset sorting arrow
    this.resetSort.next(undefined);
    this.accountsListService.getAccountList(searchValues, sort, shouldCache); // update the account list
  }

  /**
   * Sets form options from template values
   */
  private setFormOptions() {
    this.lnStatusOptions = [{ value: 0, label: this.appStrings.accounts_optionsLnAll }];
    Object.keys(this.templateValues.LnStatus).forEach(statusVal =>
      this.lnStatusOptions.push({
        value: Number(statusVal),
        label: this.templateValues.LnStatus[statusVal]
      }));

    if (!this.isAccountManager) {
      this.searchForm.controls.myCustomers.setValue(false);
    }
  }

  /**
   * Opens the advanced search dialog
   */
  openAdvancedFilteringDialog() {
    const advancedFilteringDialog = this.dialog.open(AccountsAdvancedSearchComponent, {
      autoFocus: '#customerNameField',
      panelClass: ['ds', 'fullWidth', this.deviceService.isMobile() ? 'fullScreen' : ''],
      data: {
        templateValuesUSStates: this.templateDataState,
        templateCountriesValues: this.templateCountriesValues,
        FilterType: this.templateValues.FilterType,
        values: this.searchForm.value,
        intervalOptions: this.intervalOptions,
        lnStatusOptions: this.lnStatusOptions,
        isOnline: this.isOnline
      },
    });
    advancedFilteringDialog.afterClosed().subscribe(values => {
      (document.activeElement as HTMLElement)?.blur();
      this.parseAdvancedFilteringDialogValues(values);
    });
  }

  /**
   * Parses the values from advanced filtering component
   * @param as values
   */
  private parseAdvancedFilteringDialogValues(values) {
    if (!values) {
      return;
    }

    if (values.clearAllFilters) {
      this.clearFilters();
      return;
    }

    const advancedFilteringValues = values.searchFormValue;
    const displayedValues = { ...advancedFilteringValues };

    displayedValues.accountManager = displayedValues.accountManager?.name;

    ['leads', 'accounts', 'delivery', 'myCustomers', 'filterByInterval', 'lnStatus']
      .forEach(field => delete displayedValues[field]);

    if (advancedFilteringValues.hasOwnProperty('state')) {
      delete displayedValues.state;
    }

    if (advancedFilteringValues.hasOwnProperty('country')) {
      delete displayedValues.country;
    }

    if (values.stateName) {
      displayedValues.stateName = values.stateName;
    }

    if (values.countryName) {
      displayedValues.countryName = values.countryName;
    }

    for (const prop in displayedValues) {
      if (!displayedValues[prop]) {
        delete displayedValues[prop];
      }
    }

    this.searchForm.controls.displayedSearchValue.setValue(
      this.buildDisplayedSearchValue(displayedValues, advancedFilteringValues)
    );

    this.propagateAccountManagerSelection(advancedFilteringValues.accountManager);

    this.checkAndSetSearchFields(advancedFilteringValues);

    this.searchForm.patchValue(advancedFilteringValues);
    this.searchForm.controls.stateId.setValue(advancedFilteringValues.state || null);
    this.searchForm.controls.country.setValue(advancedFilteringValues.country || null);
    this.searchForm.controls.postalCode.setValue(advancedFilteringValues.postalCode || null);
    this.searchForm.markAsDirty();

    this.searchText();
  }

  /**
   * Check if any of these filters are used (Customer / BP# / Contact) and set the searchField value accordingly
   * @param filteringValues search form values
   */
  checkAndSetSearchFields(filteringValues: any) {
    Object.keys(this.templateValues.FilterType).forEach(filter => {
      const filterBy = this.templateValues.FilterType[filter];
      if (filteringValues.hasOwnProperty(filterBy) && filteringValues[filterBy]) {
        this.searchForm.controls.searchField.setValue(filteringValues[filterBy]);
        this.searchForm.controls.searchBy.setValue(filter);
      } else {
        if (filteringValues.hasOwnProperty(filterBy) && !filteringValues[filterBy]) {
          this.searchForm.controls.searchField.setValue(null);
          this.searchForm.controls.searchBy.setValue(AccountsFilterType.Name);
        }
      }
    });
  }

  /**
   * Sets the stateId value to null if it's value is zero or empty string
   */
  private setStateIdToNullIfZeroOrEmpty() {
    const searchFormValue = this.searchForm.value;
    if (searchFormValue && (searchFormValue.stateId === 0 || searchFormValue.stateId === '')) {
      this.searchForm.controls.stateId.setValue(null);
    }
  }

  private buildDisplayedSearchValue(displayedValues: any, advancedFilteringValues: any): string {
    const summarySegments = (Object.values(displayedValues).filter(Boolean) as string[]);

    const intervalLabel = this.getIntervalLabel(advancedFilteringValues.filterByInterval);
    if (intervalLabel) {
      summarySegments.push(intervalLabel);
    }

    const lnStatusLabel = this.getLnStatusLabel(advancedFilteringValues.lnStatus);
    if (lnStatusLabel) {
      summarySegments.push(lnStatusLabel);
    }

    return summarySegments.join('; ');
  }

  private getIntervalLabel(intervalValue: string): string {
    return intervalValue && intervalValue !== 'all'
      ? this.intervalOptions.find(option => option.value === intervalValue)?.label || ''
      : '';
  }

  private getLnStatusLabel(lnStatusValue: number): string {
    return lnStatusValue
      ? this.lnStatusOptions.find(option => option.value === Number(lnStatusValue))?.label || ''
      : '';
  }

  /**
   * @param accountManager account manager returned by the advanced search dialog
   */
  private propagateAccountManagerSelection(accountManager: AccountManager) {
    if (accountManager?.employeeId === this.allAMOption.employeeId) {
      this.selectedAccountManagerService.clear();
      return;
    }

    this.selectedAccountManagerService.select(accountManager);
  }

  private normalizeAccountManager(accountManager: any) {
    if (!accountManager) {
      return null;
    }

    if (typeof accountManager === 'string') {
      if (!accountManager.trim()) {
        return null;
      }
      const selectedAccountManager = this.selectedAccountManagerService.selected;
      const storedName = selectedAccountManager?.employeeId === accountManager ? selectedAccountManager.name : '';
      return {
        employeeId: accountManager,
        name: accountManager === this.allAMOption.employeeId ? this.allAMOption.name : storedName
      };
    }

    if (!accountManager.employeeId) {
      return null;
    }

    return {
      employeeId: accountManager.employeeId,
      name: accountManager.name
    };
  }

  handleNewLead() {
    this.router.navigate(['/leads']);
  }

}
